(function () {

  // ── Helpers ───────────────────────────────────────────────────────────────

  function sanitizeValue(value, maxLength) {
    let result = (value || "")
      .replace(/[^A-Za-z0-9 '\-&,]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (maxLength && result.length > maxLength) result = result.slice(0, maxLength);
    return result;
  }

  function transformValue(value, transform) {
    if (!value) return value;
    if (transform === "uppercase") return value.toUpperCase();
    return value;
  }

  function debounce(fn, wait) {
    let t = null;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async function loadJson(url) {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
    return res.json();
  }

  async function loadFont(fontFamily, fontUrl) {
    if (!fontUrl || !fontFamily) return;
    try {
      const face = new FontFace(fontFamily, `url(${fontUrl})`);
      await face.load();
      document.fonts.add(face);
      await document.fonts.ready;
    } catch (e) {
      console.warn("[TCP] font failed:", fontFamily, e);
    }
  }

  // Returns the image URL for the currently displayed product image.
  function getCurrentVariantImageUrl() {
    if (window.tcpVariantImages) {
      const selectors = [
        'input[name="id"]:not([type="hidden"])',
        'select[name="id"]',
        'input[name="id"]',
        '[name="id"]'
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el?.value && window.tcpVariantImages[el.value]) {
          return window.tcpVariantImages[el.value].trim();
        }
      }
      const imgs = Object.values(window.tcpVariantImages).filter(Boolean);
      if (imgs.length) return imgs[0].trim();
    }

    const img =
      document.querySelector("[data-product-image-main] img.photoswipe__image") ||
      document.querySelector("[data-product-image-main] img:not([src='undefined'])") ||
      document.querySelector(".product__main-photos img.photoswipe__image");

    if (img) {
      let src = img.getAttribute("data-photoswipe-src") || img.currentSrc || img.src || "";
      if (src.startsWith("//")) src = "https:" + src;
      return src.split("?")[0] + "?width=2000";
    }

    return "";
  }

  // Returns the style name (option1) for the currently selected variant.
  // Used to look up style_colors in the config on the PHP side.
  function getCurrentVariantStyle() {
    if (!window.tcpVariantStyles) return "";
    const selectors = [
      'input[name="id"]:not([type="hidden"])',
      'select[name="id"]',
      'input[name="id"]',
      '[name="id"]'
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el?.value && window.tcpVariantStyles[el.value]) {
        return window.tcpVariantStyles[el.value].trim();
      }
    }
    const styles = Object.values(window.tcpVariantStyles).filter(Boolean);
    return styles.length ? styles[0].trim() : "";
  }

  // ── Core personalizer ─────────────────────────────────────────────────────

  async function initPersonalizer(instance) {
    const rootId     = instance.personalizerId;
    const fieldsRoot = document.querySelector(
      `.tcp-personalizer-fields[data-personalizer-id="${rootId}"]`
    );
    if (!fieldsRoot) { console.warn("[TCP] no fields root for", rootId); return; }

    const config = await loadJson(instance.configUrl);
    console.log("[TCP] config loaded:", config);

    await loadFont(config.font_family_preview, config.font_url);
    for (const field of config.text_fields) {
      if (field.font_family_preview && field.font_url_preview &&
          field.font_family_preview !== config.font_family_preview) {
        await loadFont(field.font_family_preview, field.font_url_preview);
      }
    }

    // ── Hide field groups not present in this config ───────────────────────

    const configKeys = new Set(config.text_fields.map(f => f.key));
    fieldsRoot.querySelectorAll("[data-tcp-field-group]").forEach((group) => {
      const key = group.getAttribute("data-tcp-field-group");
      if (!configKeys.has(key)) {
        group.style.display = "none";
      }
    });

    // ── Field maps ─────────────────────────────────────────────────────────

    const fieldMap  = {};
    const hiddenMap = {};
    config.text_fields.forEach((field) => {
      fieldMap[field.key] = fieldsRoot.querySelector(`[data-tcp-field="${field.key}"]`);
      hiddenMap[field.key] = fieldsRoot.querySelector(`input[type="hidden"][data-property-key="${field.key}"]`);

      // Replace the hardcoded placeholder with the config's default value,
      // so "Enter first name" becomes e.g. "Liam" matching what the canvas previews.
      if (field.default && fieldMap[field.key]) {
        fieldMap[field.key].placeholder = field.default;
      }
    });

    function syncHiddenFields() {
      config.text_fields.forEach((field) => {
        const input  = fieldMap[field.key];
        const hidden = hiddenMap[field.key];
        if (!input || !hidden) return;
        hidden.value = sanitizeValue(input.value, field.max_length);
      });
    }

    function buildValueMap() {
      const map = {};
      config.text_fields.forEach((field) => {
        const input = fieldMap[field.key];
        if (!input) return;
        const clean   = sanitizeValue(input.value, field.max_length);
        const display = transformValue(clean || field.default || "", field.transform);
        if (hiddenMap[field.key]) hiddenMap[field.key].value = clean;
        map[field.key] = { cleanValue: clean, displayValue: display };
      });
      return map;
    }

    function allFieldsEmpty() {
      return config.text_fields
        .filter(f => !f.optional)
        .every(f => {
          const input = fieldMap[f.key];
          return !input || sanitizeValue(input.value, f.max_length) === "";
        });
    }

    // ── Product root ───────────────────────────────────────────────────────

    const productRoot =
      fieldsRoot.closest("[data-section-id]") ||
      document.querySelector(`#shopify-section-${instance.sectionId}`) ||
      document.body;

    let originalImageSrc = null;

    function captureOriginalImage() {
      const img =
        productRoot.querySelector("[data-product-image-main] img.photoswipe__image") ||
        productRoot.querySelector("[data-product-image-main] img:not([src='undefined'])");
      if (img && !originalImageSrc) {
        originalImageSrc = img.src;
      }
    }

    function restoreOriginalImage() {
      if (!originalImageSrc) return;
      const imgs = productRoot.querySelectorAll(
        "[data-product-image-main] .photoswipe__image, " +
        "[data-product-image-main] img"
      );
      imgs.forEach((img) => {
        img.src = originalImageSrc;
        img.removeAttribute("data-photoswipe-src");
      });
    }

    // ── Image swap ─────────────────────────────────────────────────────────

    function swapProductImage(url) {
      const imgs = productRoot.querySelectorAll(
        "[data-product-image-main] .photoswipe__image, " +
        "[data-product-image-main] img"
      );

      const preload = new Image();
      preload.onload = function () {
        const w = preload.naturalWidth;
        const h = preload.naturalHeight;

        const apply = (img) => {
          img.src = url;
          img.removeAttribute("srcset");
          img.setAttribute("data-photoswipe-src",    url);
          img.setAttribute("data-photoswipe-width",  w);
          img.setAttribute("data-photoswipe-height", h);
        };

        if (imgs.length) {
          imgs.forEach(apply);
        } else {
          const fallback = productRoot.querySelector(
            "img.photoswipe__image:not([src='undefined'])"
          );
          if (fallback) apply(fallback);
        }
      };
      preload.src = url;
    }

    // ── Preview renderer ───────────────────────────────────────────────────

    async function renderPreview(name, secondName, imageUrl, { silent = false } = {}) {
      console.log("[TCP] renderPreview imageUrl:", imageUrl);

      const btn    = document.getElementById("tcp-preview-btn");
      const status = document.getElementById("tcp-preview-status");

      if (!silent) {
        if (btn) { btn.disabled = true; btn.textContent = "Rendering…"; }
        if (status) status.style.display = "block";
      }

      try {
        const variantStyle = getCurrentVariantStyle();
        console.log("[TCP] variant_style:", variantStyle);

        const res = await fetch("https://tcpvault.com/personalizer/preview.php", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            config_id:     config.id,
            image_url:     imageUrl,
            name,
            second_name:   secondName,
            variant_style: variantStyle
          })
        });

        const data = await res.json();
        console.log("[TCP] preview response:", data);

        if (!res.ok || data.error) {
          throw new Error(data.error || "Server error");
        }

        swapProductImage(data.url);

        if (!silent && btn) btn.dataset.rendered = "true";

      } catch (err) {
        if (!silent) {
          alert("Preview failed: " + err.message);
        }
        console.error("[TCP] preview error:", err);
      } finally {
        if (!silent) {
          if (btn) {
            btn.disabled    = false;
            btn.textContent = btn.dataset.rendered === "true"
              ? "Update"
              : "Preview";
          }
          if (status) status.style.display = "none";
        }
      }
    }

    // ── Placeholder overlay ────────────────────────────────────────────────

    async function renderPlaceholder() {
      const imageUrl = getCurrentVariantImageUrl();
      if (!imageUrl) return;

      const nameField       = config.text_fields.find(f => f.key === 'name');
      const secondNameField = config.text_fields.find(f => f.key === 'second_name');

      const placeholderName       = nameField?.default       || "Name";
      const placeholderSecondName = secondNameField?.default || "";

      console.log("[TCP] rendering placeholder with:", placeholderName, placeholderSecondName);
      await renderPreview(placeholderName, placeholderSecondName, imageUrl, { silent: true });
    }

    function attachPlaceholderClearOnInput() {
      let placeholderCleared = false;

      Object.values(fieldMap).forEach((input) => {
        if (!input) return;
        input.addEventListener("input", () => {
          if (!placeholderCleared) {
            placeholderCleared = true;
            restoreOriginalImage();
            console.log("[TCP] placeholder cleared — original image restored");
          }
        });
      });
    }

    // ── Trigger preview ───────────────────────────────────────────────────

    async function triggerPreview() {
      const valueMap   = buildValueMap();
      const name       = valueMap.name?.cleanValue        || "";
      const secondName = valueMap.second_name?.cleanValue || "";

      if (!name) {
        alert("Please enter a name first.");
        return;
      }

      for (const field of config.text_fields) {
        const value  = field.key === 'name' ? name : secondName;
        const minLen = field.min_length || 0;
        if (value && value.length < minLen) {
          alert(`${field.label || field.key} must be at least ${minLen} characters.`);
          return;
        }
        if (!value && minLen > 0 && !field.optional) {
          alert(`${field.label || field.key} must be at least ${minLen} characters.`);
          return;
        }
      }

      const imageUrl = getCurrentVariantImageUrl();
      console.log("[TCP] trigger preview — imageUrl:", imageUrl, "name:", name);

      window.tcpLastPreview = { name, secondName };
      await renderPreview(name, secondName, imageUrl);
    }

    // ── Server preview button ─────────────────────────────────────────────

    function attachServerPreviewButton() {
      const btn = document.getElementById("tcp-preview-btn");
      if (!btn) return;
      btn.addEventListener("click", triggerPreview);
    }

    // ── Enter key ─────────────────────────────────────────────────────────

    function attachEnterKeyListener() {
      Object.values(fieldMap).forEach((input) => {
        if (!input) return;
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            triggerPreview();
          }
        });
      });
    }

    // ── Re-render on style/variant change ─────────────────────────────────

    function attachVariantChangeListener() {
      const variantEvents = [
        "variant:change",
        "variant:changed",
        "theme:variant:change",
        "theme:variant:changed"
      ];

      variantEvents.forEach((eventName) => {
        document.addEventListener(eventName, () => {
          originalImageSrc = null;
          captureOriginalImage();

          if (window.tcpLastPreview) {
            setTimeout(() => {
              const { name, secondName } = window.tcpLastPreview;
              const imageUrl = getCurrentVariantImageUrl();
              renderPreview(name, secondName, imageUrl, { silent: true });
            }, 300);
          } else if (allFieldsEmpty()) {
            setTimeout(() => {
              renderPlaceholder();
            }, 300);
          }
        });
      });
    }

    // ── Form submit sync ──────────────────────────────────────────────────

    function attachFormSubmitSync() {
      const form = document.querySelector('form[action="/cart/add"]');
      if (!form) {
        console.warn("[TCP] add-to-cart form not found");
        return;
      }

      form.addEventListener("submit", () => {
        syncHiddenFields();

        // Inject system properties (_tcp_personalizer_id, _tcp_config_version).
        // These hidden inputs live outside the form in the Liquid snippet.
        const systemProps = [
          "properties[_tcp_personalizer_id]",
          "properties[_tcp_config_version]"
        ];
        systemProps.forEach((propName) => {
          const source = document.querySelector(`input[name="${propName}"]`);
          if (!source) return;
          let formInput = form.querySelector(`input[data-tcp-injected-sys="${propName}"]`);
          if (!formInput) {
            formInput = document.createElement("input");
            formInput.type = "hidden";
            formInput.name = propName;
            formInput.dataset.tcpInjectedSys = propName;
            form.appendChild(formInput);
          }
          formInput.value = source.value;
          console.log("[TCP] injected sys prop:", propName, "=", source.value);
        });

        // Inject text field properties (Name, Second Name, etc.)
        config.text_fields.forEach((field) => {
          const hidden = hiddenMap[field.key];
          if (!hidden) return;

          const propName = hidden.name;
          const value    = hidden.value;

          let formInput = form.querySelector(`input[data-tcp-injected="${field.key}"]`);
          if (!formInput) {
            formInput                     = document.createElement("input");
            formInput.type                = "hidden";
            formInput.name                = propName;
            formInput.dataset.tcpInjected = field.key;
            form.appendChild(formInput);
          }
          formInput.value = value;
          console.log("[TCP] injected into form:", propName, "=", value);
        });
      });

      console.log("[TCP] form submit sync attached");
    }

    // ── Hidden field sync (ongoing, as user types) ────────────────────────

    const debouncedSync = debounce(syncHiddenFields, 100);
    Object.values(fieldMap).forEach((input) => {
      if (input && !input.dataset.tcpPreviewBound) {
        input.addEventListener("input",  debouncedSync);
        input.addEventListener("change", debouncedSync);
        input.dataset.tcpPreviewBound = "true";
      }
    });

    syncHiddenFields();

    // ── Attach all listeners ───────────────────────────────────────────────

    attachServerPreviewButton();
    attachEnterKeyListener();
    attachVariantChangeListener();
    attachPlaceholderClearOnInput();
    attachFormSubmitSync();

    // ── Render placeholder on load if fields are empty ────────────────────

    captureOriginalImage();

    if (allFieldsEmpty()) {
      setTimeout(() => {
        captureOriginalImage();
        renderPlaceholder();
      }, 800);
    }
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  //
  // When the script is loaded with `defer`, DOMContentLoaded may have already
  // fired by the time the script executes. Check readyState and run immediately
  // if the DOM is already ready, otherwise wait for the event.

  function bootPersonalizer() {
    const seen = new Set();

    document.querySelectorAll(
      ".tcp-personalizer-fields[data-personalizer-id]"
    ).forEach((root) => {
      const id  = root.getAttribute("data-personalizer-id");
      const url = `https://tcpvault.com/personalizer/config/${id}.json`;

      if (!id || seen.has(id)) return;
      seen.add(id);

      initPersonalizer({ personalizerId: id, configUrl: url })
        .catch((e) => console.error("[TCP] init failed:", e));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootPersonalizer);
  } else {
    bootPersonalizer();
  }

})();